/**
 * @file useConversation.ts
 * @description Данные открытого диалога (chat-window-design.md §3, §5, §6):
 * карточка, лента страницами, долгий опрос новых сообщений и записей.
 * Логика загрузки и опроса — из прежнего `ChatPanel` (phase10.7).
 *
 * Лента (cabinet-api-contract §12, «чат-2»): при открытии — последние
 * сообщения; «Показать ранние сообщения» дочитывает предыдущую страницу по
 * курсору `before` (`nextCursor`), пока `hasMore`. Страницы, опрос и
 * отправка сливаются без повторов (`mergeFeedItems`).
 *
 * «Прочитано» — отдельный хук `useConversationRead`. Пришла служебная запись
 * (передали, вернули) — карточка перечитывается: сменился ответственный и
 * действия. Запись «Передан вам», с которой диалог открыт или которая пришла
 * при открытом диалоге, запоминается до смены диалога (`transferredToMe`):
 * отметка «прочитано» гасит признак на сервере сразу, а полоса над лентой
 * должна остаться, пока человек её не закроет или не ответит.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  adminChat,
  type ChatFeedItem,
  type ChatMessageDto,
  type ConversationDetail,
  type ConversationEventDto,
} from "../../lib/api-chat";
import { useNotify } from "../ui/NotifyProvider";
import { isLostConversation } from "./chat-errors";
import { CHAT_TEXT } from "./chat-texts";
import { firstUnreadMessageId, hasCustomerMessage, lastFeedAt, mergeFeed, mergeFeedItems } from "./feed-merge";
import { useConversationRead } from "./useConversationRead";

const POLL_RETRY_MS = 2_000;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      window.clearTimeout(timer);
      resolve();
    });
  });
}

/** Запись «Передан вам» в карточке — если признак горит. */
function transferToMe(card: ConversationDetail): ConversationEventDto | null {
  return card.flags.transferredToMe && card.lastEvent?.type === "TRANSFERRED" ? card.lastEvent : null;
}

export function useConversation(id: string | null, visible: boolean, onRead: () => void) {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [feed, setFeed] = useState<ChatFeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [lost, setLost] = useState(false);
  const [firstUnreadId, setFirstUnreadId] = useState<string | null>(null);
  // Курсор ранних сообщений: null — показано всё с начала переписки.
  const [earlierCursor, setEarlierCursor] = useState<string | null>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [transferredToMe, setTransferredToMe] = useState<ConversationEventDto | null>(null);
  const [customerTick, setCustomerTick] = useState(0);
  const [tick, setTick] = useState(0);
  const feedRef = useRef(feed);
  feedRef.current = feed;
  const earlierController = useRef<AbortController | null>(null);
  const notify = useNotify();

  /** Новая карточка: запомнить передачу мне, если она есть. */
  const acceptCard = useCallback((card: ConversationDetail) => {
    setDetail(card);
    const transfer = transferToMe(card);
    if (transfer) setTransferredToMe(transfer);
  }, []);

  const refreshDetail = useCallback(async () => {
    if (!id) return;
    try {
      acceptCard(await adminChat.findOne(id));
    } catch (e) {
      if (isLostConversation(e)) setLost(true);
    }
  }, [id, acceptCard]);

  // Карточка и последняя страница ленты при выборе диалога.
  useEffect(() => {
    setDetail(null);
    setFeed([]);
    setLost(false);
    setFirstUnreadId(null);
    setEarlierCursor(null);
    setLoadingEarlier(false);
    setTransferredToMe(null);
    earlierController.current?.abort();
    if (!id) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    Promise.all([adminChat.findOne(id, controller.signal), adminChat.feed(id, {}, controller.signal)])
      .then(([card, page]) => {
        acceptCard(card);
        setFeed(page.items);
        setEarlierCursor(page.hasMore ? page.nextCursor : null);
        setFirstUnreadId(firstUnreadMessageId(page.items, card.unreadCount));
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        if (isLostConversation(e)) setLost(true);
        else setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [id, tick, acceptCard]);

  // Долгий опрос: новые сообщения и служебные записи.
  const ready = detail !== null && detail.id === id;
  useEffect(() => {
    if (!id || !ready) return;
    const controller = new AbortController();
    const loop = async () => {
      while (!controller.signal.aborted) {
        try {
          const since = lastFeedAt(feedRef.current, new Date(0).toISOString());
          const res = await adminChat.poll(id, since, controller.signal);
          if (controller.signal.aborted) return;
          if (res.messages.length > 0 || res.events.length > 0) {
            setFeed((prev) => mergeFeed(prev, res.messages, res.events));
          }
          if (hasCustomerMessage(res.messages)) setCustomerTick((n) => n + 1);
          if (res.events.length > 0) void refreshDetail();
        } catch (e) {
          if (controller.signal.aborted) return;
          if (isLostConversation(e)) {
            setLost(true);
            return;
          }
          await sleep(POLL_RETRY_MS, controller.signal);
        }
      }
    };
    void loop();
    return () => controller.abort();
  }, [id, ready, refreshDetail]);

  useConversationRead({ id, detail, visible, customerTick, refreshDetail, onRead });

  // Окно закрыли или диалог размонтирован — дочитку ранних прерываем.
  useEffect(() => () => earlierController.current?.abort(), []);

  const loadEarlier = useCallback(() => {
    if (!id || !earlierCursor || loadingEarlier) return;
    const controller = new AbortController();
    earlierController.current = controller;
    setLoadingEarlier(true);
    adminChat
      .feed(id, { before: earlierCursor }, controller.signal)
      .then((page) => {
        setFeed((prev) => mergeFeedItems(page.items, prev));
        setEarlierCursor(page.hasMore ? page.nextCursor : null);
      })
      .catch(() => {
        if (!controller.signal.aborted) notify.error(CHAT_TEXT.earlierFailed);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingEarlier(false);
        if (earlierController.current === controller) earlierController.current = null;
      });
  }, [id, earlierCursor, loadingEarlier, notify]);

  const mergeMessages = useCallback((messages: ChatMessageDto[], events: ConversationEventDto[] = []) => {
    setFeed((prev) => mergeFeed(prev, messages, events));
  }, []);

  const retry = useCallback(() => setTick((n) => n + 1), []);

  return {
    detail,
    feed,
    hasEarlier: earlierCursor !== null,
    loadingEarlier,
    loadEarlier,
    loading,
    error,
    lost,
    firstUnreadId,
    transferredToMe,
    setDetail,
    refreshDetail,
    mergeMessages,
    retry,
  };
}

export type UseConversation = ReturnType<typeof useConversation>;
